import styles from "./SignUpAddInfo.module.css";
import { Color, path } from "../common/Common";
import { ChangeEvent, useEffect, useRef, useState, BaseSyntheticEvent } from "react";
import Button from "../common/Button";
import PolicyModal from "./PolicyModal";
import PrivacyModal from "./PrivacyModal";
import { AccountManager } from "../../workers/AccountManager";
import { useNavigate } from "react-router-dom";

import { useModal } from "../modal/Modal";
import ModalButtons from "../modal/ModalButtons";
import { useLoginStatus } from "../../hooks/useLoginStatus";

/**
 * @author kmy
 * @date 2025-03-17
 * @remark 추가정보 입력 및 약관 동의 페이지
 * @returns <section>
 */

function SignUpAddInfo() {
    const { ModalComponent, openModal, closeModal } = useModal();
    const navigate = useNavigate();
    const isLogin = useLoginStatus();

    // 로그인 되면 라우팅
    useEffect(() => {
        if (isLogin) {
            navigate(path.ROOT, { replace: true });
        }
    }, [isLogin, navigate]);

    const showModal = (message: string) => {
        openModal({
            title: "안내",
            children: message,
            modalButtons: (
                <ModalButtons
                    buttonCount={1}
                    buttons={[
                        {
                            label: "확인",
                            color: "Primary",
                            onClick: () => closeModal(),
                        },
                    ]}
                />
            ),
        });
    };

    // const authJson = localStorage.getItem("authJson");
    // let platformName, password, id, host, name, year, month, day, sex;

    // if (authJson) {
    //     const parsedAuth = JSON.parse(authJson);
    //     platformName = parsedAuth.platformName || "";
    //     password = parsedAuth.password || "";
    //     id = parsedAuth.email || "";
    //     host = parsedAuth.host || "";
    //     name = parsedAuth.name || "";
    //     year = parsedAuth.year != null ? parsedAuth.year : "";
    //     month = parsedAuth.month != null ? parsedAuth.month : "";
    //     day = parsedAuth.day != null ? parsedAuth.day : "";
    //     sex = parsedAuth.sex != null ? parsedAuth.sex : "";
    // }

    // const [registerInfo, setRegisterInfo] = useState({
    //     platform: (() => {
    //         if (platformName === "GOOGLE") {
    //             return 2;
    //         } else if (platformName === "NAVER") {
    //             return 3;
    //         } else if (platformName === "KAKAO") {
    //             return 4;
    //         } else if (platformName === "FACEBOOK") {
    //             return 5;
    //         } else {
    //             return 1;
    //         }
    //     })(),
    //     password: password,
    //     id: id,
    //     host: host,
    //     name: name,
    //     year: year != "" ? Number(year) : "",
    //     month: month != "" ? Number(month) : "",
    //     day: day != "" ? Number(day) : "",
    //     sex: sex,
    //     policyAccepted: false, // 약관 동의
    //     privacyAccepted: false, // 개인정보 수집 동의
    // });

    // 소셜로그인 시도 후에 localStorage에 저장한 소셜플랫폼의 auth정보를 삭제하도록 변경 | 250901 | smyun
    const [registerInfo, setRegisterInfo] = useState({
        platform: 1, // 기본값
        password: "",
        id: "",
        host: "",
        name: "",
        year: "", // 숫자 안 넣고, 문자열 빈값으로 시작
        month: "",
        day: "",
        sex: "1", // 기본값 "남"이면 "1"
        policyAccepted: false,
        privacyAccepted: false,
    });

    useEffect(() => {
        const raw = localStorage.getItem("authJson");
        if (!raw) return;

        try {
            const parsed = JSON.parse(raw);
            const platformName = parsed.platformName || "";
            const mapPlatform =
                platformName === "GOOGLE" ? 2 : platformName === "NAVER" ? 3 : platformName === "KAKAO" ? 4 : platformName === "FACEBOOK" ? 5 : 1;

            setRegisterInfo((prev) => ({
                ...prev,
                platform: mapPlatform,
                password: parsed.password || "",
                id: parsed.email || "",
                host: parsed.host || "",
                name: parsed.name || "",
                year: parsed.year != null ? String(parsed.year) : "",
                month: parsed.month != null ? String(parsed.month) : "",
                day: parsed.day != null ? String(parsed.day) : "",
                sex: parsed.sex != null ? String(parsed.sex) : prev.sex,
            }));
        } catch (e) {
            // 파싱 실패 시 무시
        } finally {
            // 상태로 옮긴 후 즉시 삭제 (보안상 최소 노출)
            localStorage.removeItem("authJson");
        }
    }, []);

    const [isValidName, setIsValidName] = useState(true);
    const handleFormChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        var key = e.target.name;
        var value = e.target.value;
        setRegisterInfo({
            ...registerInfo,
            [key]: value,
        });
    };

    // 이용약관, 개인정보 수집동의 체크박스 클릭
    const checkBoxClicked = (e: ChangeEvent<HTMLInputElement>) => {
        var key = e.target.name;
        var checked = e.target.checked;

        setRegisterInfo({
            ...registerInfo,
            [key]: checked,
        });
    };

    // 회원가입 버튼
    const registerBtnClicked = async (e: BaseSyntheticEvent) => {
        if (registerInfo.year == "") {
            showModal("생년월일을 입력해주세요.");
        } else if (!registerInfo.policyAccepted) {
            showModal("모두의 지도 이용약관에 동의해주세요.");
        } else if (!registerInfo.privacyAccepted) {
            showModal("개인정보 수집 및 이용약관에 동의해주세요.");
        } else {
            const accountManager = AccountManager.getInstance();
            const registerUserResult = await accountManager.registerUser(registerInfo);

            if (registerUserResult.flag) {
                localStorage.removeItem("authJson"); // 가입 완료 시 삭제(보강)

                openModal({
                    title: "안내",
                    children: (
                        <>
                            회원가입이 완료되었습니다.
                            <br />
                            메인페이지에서 로그인해주세요.
                        </>
                    ),
                    modalButtons: (
                        <ModalButtons
                            buttonCount={1}
                            buttons={[
                                {
                                    label: "확인",
                                    color: "Primary",
                                    onClick: () => {
                                        closeModal();
                                        navigate("/");
                                    },
                                },
                            ]}
                        />
                    ),
                });
            } else {
                showModal("회원가입에 실패하였습니다.");
            }
        }
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.tit_bx}>
                <h2 className={[styles.tit, styles.blue].join(" ")}>추가정보 입력 및 약관 동의</h2>
            </div>

            <div className={styles.regist_bx}>
                <div className={`${styles.container} ${styles.row}`}>
                    <div className={styles.info_tit_bx}>
                        <p className={styles.info_tit}>
                            성별 <span className={styles.required}>*</span>
                        </p>
                    </div>
                    <div className={`${styles.row}`}>
                        <select name="sex" onChange={handleFormChange} className={`${styles.select_box}`} value={registerInfo.sex}>
                            <option value="1" selected>
                                남
                            </option>
                            <option value="0">여</option>
                        </select>
                    </div>
                </div>
                <div className={`${styles.container} ${styles.row}`}>
                    <div className={styles.info_tit_bx}>
                        <p className={styles.info_tit}>
                            생년월일 <span className={styles.required}>*</span>
                        </p>
                    </div>
                    <div className={`${styles.row}`}>
                        <select name="year" onChange={handleFormChange} className={`${styles.select_box}`} value={registerInfo.year}>
                            <option value="" disabled selected>
                                년
                            </option>
                            {Array.from({ length: new Date().getFullYear() - 1900 + 1 }, (_, index) => 1900 + index)
                                .reverse()
                                .map((date) => (
                                    <option key={date} value={date}>
                                        {date}
                                    </option>
                                ))}
                        </select>
                        <select name="month" onChange={handleFormChange} className={`${styles.select_box}`} value={registerInfo.month}>
                            <option value="" disabled selected>
                                월
                            </option>
                            {Array.from({ length: 12 }, (_, index) => index + 1).map((date) => (
                                <option key={date} value={date}>
                                    {date}
                                </option>
                            ))}
                        </select>
                        <select name="day" onChange={handleFormChange} className={`${styles.select_box}`} value={registerInfo.day}>
                            <option value="" disabled selected>
                                일
                            </option>
                            {Array.from({ length: 31 }, (_, index) => index + 1).map((date) => (
                                <option key={date} value={date}>
                                    {date}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
            <div className={`${styles.accepted_container}`}>
                <div className={`${styles.accepted_bx} ${styles.row}`}>
                    <label>
                        모두의 지도 서비스 이용약관 동의
                        <input
                            type="checkbox"
                            name="policyAccepted"
                            checked={registerInfo.policyAccepted}
                            onClick={() =>
                                openModal({
                                    title: "모두의 지도 서비스 이용약관 동의",
                                    children: <PolicyModal checkBoxClicked={checkBoxClicked} isAccepted={registerInfo.policyAccepted} />,
                                    modalButtons: (
                                        <ModalButtons
                                            buttonCount={1}
                                            buttons={[
                                                {
                                                    label: "확인",
                                                    color: "Primary",
                                                    onClick: () => closeModal(),
                                                },
                                            ]}
                                        />
                                    ),
                                    size: "wide",
                                })
                            }
                        />
                    </label>
                </div>
                <div className={`${styles.accepted_bx} ${styles.row}`}>
                    <label>
                        개인정보 수집 및 이용에 대한 안내
                        <input
                            type="checkbox"
                            name="privacyAccepted"
                            checked={registerInfo.privacyAccepted}
                            onClick={() =>
                                openModal({
                                    title: "개인정보 수집 및 이용에 대한 안내",
                                    children: (
                                        <PrivacyModal
                                            onClose={() => closeModal()}
                                            checkBoxClicked={checkBoxClicked}
                                            isAccepted={registerInfo.privacyAccepted}
                                        />
                                    ),
                                    modalButtons: (
                                        <ModalButtons
                                            buttonCount={1}
                                            buttons={[
                                                {
                                                    label: "확인",
                                                    color: "Primary",
                                                    onClick: () => closeModal(),
                                                },
                                            ]}
                                        />
                                    ),
                                    size: "wide",
                                })
                            }
                        />
                    </label>
                </div>
            </div>
            <div className={`${styles.signup_btn_container}`} style={{ marginTop: "30px" }}>
                <button onClick={registerBtnClicked}>회원가입</button>
            </div>

            <ModalComponent />
        </div>
    );
}

export default SignUpAddInfo;
